import { fromNano } from "@ton/core";
import { useTonAddress } from "@tonconnect/ui-react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NFTStatus } from "../../../../../../../entities/NFT/NFTTypes";
import { Ticker } from "../../../../../../../entities/Ticker/TickerTypes";
import { useUserCartStore } from "../../../../../../../features/UserCart/helpers/useUserCartStore";
import useLaunchSale from "../../../../../../../hooks/useLaunchSale";
import { useConvertionRatesStore } from "../../../../../../../stores/ConvertionRatesStore/useConvertionRatesStore";
import useTonClientStore from "../../../../../../../stores/TonClientStore/useTonClientStore";
import {
  getOtherMarketplaceNetworkFee,
  networkFeeValue,
} from "../../../../../../../utils/AppConstants";
import Btn from "../../../../../../Btn/Btn";
import { EmptyContainer } from "../../../../../../EmptyContainer/EmptyContainer";
import { Loader } from "../../../../../../Icons/Loader";
import { InsufficientFunds } from "../../../../../../InsufficientFundsComponent/InsufficientFunds";
import MainHeaderStore from "../../../../../stores/MainHeaderStore";
import CartItem from "../CartItem/CartItem";
import s from "./CartList.module.scss";

type Props = {
  headerStore: MainHeaderStore;
};

export const CartList: React.FC<Props> = observer(({ headerStore }) => {
  const cartStore = useUserCartStore();
  const { tonToUsdtConvertionRate } = useConvertionRatesStore();
  const tonClientStore = useTonClientStore();
  const userAddress = useTonAddress();

  const { purchaseCartItems } = useLaunchSale();
  const navigate = useNavigate();

  const calculateSum = useMemo(() => {
    return cartStore.cart.reduce((acc, item) => {
      const price =
        item.status === NFTStatus.ON_SALE
          ? fromNano(item.currentSale?.price ?? "")
          : fromNano(item.currentOtherSale?.price ?? "");
      return acc + Number(price);
    }, 0);
  }, [cartStore.cart.length]);

  const calculateNetworkFees = useMemo(() => {
    return cartStore.cart.reduce((acc, item) => {
      const price =
        item.status === NFTStatus.ON_SALE
          ? networkFeeValue
          : getOtherMarketplaceNetworkFee(
              item?.currentOtherSale?.contractType ?? "gg_sale_v3_3"
            );
      return acc + Number(price);
    }, 0);
  }, [cartStore.cart.length]);

  const calculateUsdSum = useMemo(() => {
    if (tonToUsdtConvertionRate && calculateSum && calculateNetworkFees) {
      return (
        tonToUsdtConvertionRate *
        (calculateSum + calculateNetworkFees)
      ).toFixed(2);
    }
  }, [calculateSum, calculateNetworkFees, tonToUsdtConvertionRate]);

  const fetchBalance = async () => {
    await tonClientStore.loadTonBalance(userAddress);
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted && headerStore.isCartVisible && !tonClientStore.tonBalance) {
      fetchBalance();
    }

    return () => {
      isMounted = false;
    };
  }, [tonClientStore.tonBalance, userAddress, headerStore.isCartVisible]);

  if (cartStore.cart.length === 0) {
    return (
      <div className={s.empty_wrapper}>
        <EmptyContainer
          title="Add items to get started."
          subtitle="Check back later."
        />
      </div>
    );
  }

  return (
    <div className={s.cart_wrapper}>
      <div className={s.cart_info}>
        <p>{cartStore.cart.length} items</p>
        <div
          onClick={() => {
            cartStore.resetCart();
            headerStore.setCartVisible(false);
          }}
        >
          Clear All
        </div>
      </div>
      <div className={s.cart_list}>
        {cartStore.cart.map((item) => (
          <CartItem
            key={item.address}
            address={item.address}
            collectionTitle={item.collection?.name}
            image={item.nftItem.previews.at(1)?.url}
            price={
              item.status === NFTStatus.ON_SALE
                ? fromNano(item.currentSale?.price ?? "")
                : fromNano(item.currentOtherSale?.price ?? "")
            }
            ticker={item.currentSale?.ticker ?? "TON"}
            title={item.nftItem.metadata.name}
            removeHandler={(address) => cartStore.removeItemFromCart(address)}
          />
        ))}
      </div>
      <div className={s.network_fees_wrapper}>
        <div className={s.network_fees_typo}>
          <p className={s.network_fees_title}>Network Fee</p>
          <p className={s.network_fees_description}>
            The rest will be returned to your wallet
          </p>
        </div>
        <div className={s.network_fees_amount}>
          {calculateNetworkFees % 1 === 0
            ? calculateNetworkFees
            : calculateNetworkFees.toFixed(2)}{" "}
          TON
        </div>
      </div>
      <div className={s.price_wrapper}>
        <div className={s.price_amount}>
          <p>Total price</p>
          <p>
            {(calculateSum + calculateNetworkFees) % 1 === 0
              ? calculateSum + calculateNetworkFees
              : (calculateSum + calculateNetworkFees).toFixed(2)}{" "}
            TON
          </p>
        </div>
        {calculateUsdSum && (
          <div className={s.price_usd}>
            <p>${calculateUsdSum}</p>
          </div>
        )}
      </div>
      {tonClientStore.tonBalance &&
        tonClientStore.tonBalance < calculateSum + calculateNetworkFees && (
          <InsufficientFunds ticker={Ticker.TON} />
        )}
      <div>
        <Btn
          onClick={async () => {
            headerStore.setCartVisible(false);
            cartStore.setModalVisibility(true);
            await purchaseCartItems(cartStore.cart);
            cartStore.resetModalFlow();
            navigate(`/cart/congrats`);
          }}
          disabled={
            tonClientStore.isBalanceFetchLoading
              ? true
              : tonClientStore?.tonBalance
              ? tonClientStore.tonBalance < calculateSum + calculateNetworkFees
              : true
          }
        >
          {tonClientStore.isBalanceFetchLoading ? <Loader /> : "Purchase"}
        </Btn>
      </div>
    </div>
  );
});
